'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useMemo, useRef } from 'react';
const ModalManagerContext = createContext(undefined);
/**
 * Saves the current document position, moves to the modal view, and restores
 * the saved position after the last ModalSidekick unsubscribes.
 * The provider must wrap routes that use ModalSidekick.
 *
 * @param root0 props
 * @param root0.children descendants that can use the modal manager
 * @returns modal manager context provider
 */
export function ModalManagerProvider({ children }) {
    const activeLocks = useRef(new Set());
    const previousDocumentState = useRef(undefined);
    const pendingScrollY = useRef(undefined);
    const modalTrigger = useRef(undefined);
    const modalDialogs = useRef(new Set());
    const prepareModalOpen = useCallback((trigger) => {
        pendingScrollY.current = window.scrollY;
        modalTrigger.current = trigger;
    }, []);
    const registerModalDialog = useCallback((dialog) => {
        modalDialogs.current.add(dialog);
        return () => {
            modalDialogs.current.delete(dialog);
        };
    }, []);
    const focusModalDialog = useCallback(() => {
        const dialog = Array.from(modalDialogs.current).at(-1);
        dialog?.focus({ preventScroll: true });
    }, []);
    const enterModalView = useCallback(() => {
        const token = Symbol('modal-scroll-lock');
        if (activeLocks.current.size === 0) {
            previousDocumentState.current = {
                scrollY: pendingScrollY.current ?? window.scrollY,
            };
            pendingScrollY.current = undefined;
            window.scrollTo({ top: 0, behavior: 'instant' });
        }
        activeLocks.current.add(token);
        return token;
    }, []);
    const exitModalView = useCallback((token) => {
        if (!activeLocks.current.delete(token))
            return;
        if (activeLocks.current.size === 0 && previousDocumentState.current) {
            const previousState = previousDocumentState.current;
            previousDocumentState.current = undefined;
            window.scrollTo({ top: previousState.scrollY, behavior: 'instant' });
            const trigger = modalTrigger.current;
            modalTrigger.current = undefined;
            if (trigger?.isConnected) {
                queueMicrotask(() => {
                    trigger.focus({ preventScroll: true });
                });
            }
        }
    }, []);
    const manager = useMemo(() => ({ prepareModalOpen, registerModalDialog, focusModalDialog, enterModalView, exitModalView }), [prepareModalOpen, registerModalDialog, focusModalDialog, enterModalView, exitModalView]);
    return _jsx(ModalManagerContext.Provider, { value: manager, children: children });
}
/**
 * Returns the modal manager operations.
 *
 * @returns modal manager operations for opening and subscribing to modal view
 * @throws {Error} when used outside a ModalManagerProvider
 */
export function useModalManager() {
    const manager = useContext(ModalManagerContext);
    if (!manager) {
        throw new Error('Modal components must be rendered within a ModalManagerProvider');
    }
    return manager;
}
//# sourceMappingURL=ModalManagerProvider.js.map